import React, { useMemo, useState } from "react";
import * as Phosphor from "@phosphor-icons/react";
import { TABS } from "@/data/lexikon";

export default function Lexikon() {
  const [tabId, setTabId] = useState("tabellen");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const activeTab = TABS.find((t) => t.id === tabId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeTab.items;
    return activeTab.items.filter((it) => {
      const hay = [
        it.title,
        it.summary,
        it.details,
        it.tip,
        (it.tags || []).join(" "),
        (it.badges || []).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [activeTab, search]);

  // global search across all tabs when search is set
  const globalHits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return TABS.map((t) => ({
      tab: t,
      count: t.items.filter((it) =>
        [it.title, it.summary, it.details, it.tip, (it.tags || []).join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(q)
      ).length,
    }));
  }, [search]);

  return (
    <div className="space-y-6" data-testid="lexikon-root">
      <header>
        <div className="eid-label">Wissen · Für Landwirte &amp; Förster</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Agrar-Lexikon</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Alle Dashboard-Werte, Düngemittel, Feldfrüchte und Bearbeitungsmaßnahmen — in
          verständlicher Sprache und direkt verknüpft mit den Daten, die du hier siehst.
        </p>
      </header>

      {/* Tabs + Search */}
      <div className="eid-card p-3 space-y-3" data-testid="lexikon-controls">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = Phosphor[t.icon] || Phosphor.Book;
            const active = t.id === tabId;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setTabId(t.id);
                  setExpandedId(null);
                }}
                data-testid={`lexikon-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm transition-all ${
                  active
                    ? "border-white/15 shadow-sm"
                    : "border-white/10 hover:bg-white/5"
                }`}
                style={
                  active
                    ? { background: t.accent, color: "#FFFFFF", borderColor: t.accent }
                    : { background: "#121212", color: "#FFFFFF" }
                }
              >
                <Icon size={16} />
                <span>{t.title}</span>
                <span
                  className="text-[10px] font-mono ml-1 px-1.5 py-0.5 rounded-full"
                  style={{
                    background: active ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)",
                    color: active ? "#FFFFFF" : "#A1A1AA",
                  }}
                >
                  {t.items.length}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 border border-white/10 rounded-full bg-[#121212] px-3 py-2">
          <Phosphor.MagnifyingGlass size={16} className="text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Suche: z.B. „Stickstoff", „Weizen", „NDVI", „Bewässerung"…'
            data-testid="lexikon-search"
            className="flex-1 bg-transparent text-sm focus:outline-none text-white placeholder:text-zinc-500"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="lexikon-clear-search"
              className="text-zinc-500 hover:text-white"
            >
              <Phosphor.X size={14} />
            </button>
          )}
        </div>
        {globalHits && (
          <div className="flex flex-wrap gap-1.5 text-[11px] font-mono" data-testid="lexikon-global-hits">
            <span className="text-zinc-400 uppercase tracking-wider self-center">Treffer:</span>
            {globalHits.map((h) => (
              <button
                key={h.tab.id}
                onClick={() => setTabId(h.tab.id)}
                disabled={h.count === 0}
                className={`px-2 py-1 rounded-full border transition-colors ${
                  h.count === 0
                    ? "border-white/10 text-zinc-500 bg-transparent cursor-default"
                    : h.tab.id === tabId
                    ? "border-white/15 bg-[#F5F5F5] text-[#0A0A0A]"
                    : "border-white/10 bg-[#121212] text-white hover:bg-white/5"
                }`}
                data-testid={`lexikon-hit-${h.tab.id}`}
              >
                {h.tab.title} · {h.count}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Items */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="lexikon-items">
        {filtered.length === 0 ? (
          <div className="eid-card p-6 text-sm text-zinc-400 col-span-full" data-testid="lexikon-empty">
            Keine Treffer für „{search}" in {activeTab.title}. Probier einen anderen Tab
            oder ein kürzeres Stichwort.
          </div>
        ) : (
          filtered.map((it) => (
            <LexikonCard
              key={it.id}
              item={it}
              accent={activeTab.accent}
              expanded={expandedId === it.id}
              onToggle={() => setExpandedId(expandedId === it.id ? null : it.id)}
            />
          ))
        )}
      </section>
    </div>
  );
}

function LexikonCard({ item, accent, expanded, onToggle }) {
  const Icon = Phosphor[item.icon] || Phosphor.Info;
  return (
    <div
      className="eid-card p-4 md:p-5 transition-all"
      data-testid={`lexikon-card-${item.id}`}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <button
        onClick={onToggle}
        className="w-full text-left flex items-start gap-3"
        data-testid={`lexikon-toggle-${item.id}`}
      >
        <span
          className="shrink-0 p-2 rounded-sm"
          style={{ background: `${accent}22`, color: accent }}
        >
          <Icon size={24} weight="regular" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-medium tracking-tight">{item.title}</h3>
            <Phosphor.CaretDown
              size={16}
              className="text-zinc-500 transition-transform shrink-0"
              style={{ transform: expanded ? "rotate(180deg)" : "rotate(0)" }}
            />
          </div>
          <p className="text-sm text-zinc-400 mt-1">{item.summary}</p>
          {!expanded && (item.tags?.length || item.badges?.length) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(item.tags || []).map((t) => (
                <span
                  key={t}
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/10 text-zinc-400"
                >
                  {t}
                </span>
              ))}
              {(item.badges || []).map((b) => (
                <span
                  key={b}
                  className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                  style={{ background: `${accent}22`, color: accent }}
                >
                  → {b}
                </span>
              ))}
            </div>
          )}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 pl-14 space-y-4" data-testid={`lexikon-body-${item.id}`}>
          <div className="text-sm text-white leading-relaxed whitespace-pre-line">
            {item.details}
          </div>

          {item.tip && (
            <div
              className="flex items-start gap-2 p-3 rounded-sm"
              style={{ background: `${accent}14`, borderLeft: `3px solid ${accent}` }}
            >
              <Phosphor.Lightbulb size={18} style={{ color: accent }} className="mt-0.5 shrink-0" />
              <div>
                <div
                  className="text-[10px] font-mono uppercase tracking-wider"
                  style={{ color: accent }}
                >
                  Praxis-Tipp
                </div>
                <div className="text-sm text-white mt-0.5">{item.tip}</div>
              </div>
            </div>
          )}

          {(item.tags?.length || item.badges?.length) && (
            <div className="flex flex-wrap gap-1.5">
              {(item.tags || []).map((t) => (
                <span
                  key={t}
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/10 text-zinc-400"
                >
                  {t}
                </span>
              ))}
              {(item.badges || []).map((b) => (
                <span
                  key={b}
                  className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                  style={{ background: `${accent}22`, color: accent }}
                >
                  → {b}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
